import React, { useState, useRef } from 'react';

const ModernSidebar = ({
  projects,
  currentProject,
  files,
  currentFile,
  onSelectProject,
  onSelectFile,
  onCreateProject,
  onCreateFile,
  onUploadFile,
  onImportGitHub,
  onOpenTemplates,
  onOpenSync
}) => {
  const [activeTab, setActiveTab] = useState('files'); // files, ai, build, git
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const fileInputRef = useRef(null);

  const tabs = [
    { id: 'files', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z', label: 'Files' },
    { id: 'ai', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z', label: 'AI' },
    { id: 'build', icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4', label: 'Build' },
    { id: 'git', icon: 'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', label: 'Git' }
  ];

  const filterFiles = (files, query) => {
    if (!query) return files;
    return files.filter(f =>
      f.name.toLowerCase().includes(query.toLowerCase()) ||
      f.path.toLowerCase().includes(query.toLowerCase())
    );
  };

  const buildFileTree = (files) => {
    const tree = {};

    files.forEach(file => {
      const parts = file.path.split('/');
      let current = tree;

      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          // It's a file
          if (!current._files) current._files = [];
          current._files.push(file);
        } else {
          // It's a folder
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
      });
    });

    return tree;
  };

  const toggleFolder = (path) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const renderFileTree = (node, path = '', level = 0) => {
    const items = [];

    // Render folders
    Object.keys(node).forEach(key => {
      if (key === '_files') return;

      const folderPath = path ? `${path}/${key}` : key;
      const isExpanded = expandedFolders.has(folderPath);

      items.push(
        <div key={folderPath}>
          <div
            onClick={() => toggleFolder(folderPath)}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-800 cursor-pointer text-sm"
            style={{ paddingLeft: `${level * 12 + 12}px` }}
          >
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <span className="text-gray-300">{key}</span>
          </div>
          {isExpanded && renderFileTree(node[key], folderPath, level + 1)}
        </div>
      );
    });

    // Render files
    if (node._files) {
      node._files.forEach(file => {
        const isActive = currentFile?.id === file.id;
        const fileIcon = getFileIcon(file.name);

        items.push(
          <div
            key={file.id}
            onClick={() => onSelectFile(file)}
            className={`flex items-center gap-2 px-3 py-1.5 hover:bg-gray-800 cursor-pointer text-sm ${
              isActive ? 'bg-blue-900 bg-opacity-30 border-l-2 border-blue-500' : ''
            }`}
            style={{ paddingLeft: `${level * 12 + 28}px` }}
          >
            <span className="text-lg">{fileIcon}</span>
            <span className={`flex-1 truncate ${isActive ? 'text-blue-400 font-medium' : 'text-gray-300'}`}>
              {file.name}
            </span>
            {file.modified_locally && (
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
            )}
          </div>
        );
      });
    }

    return items;
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const iconMap = {
      js: '📄',
      jsx: '⚛️',
      ts: '📘',
      tsx: '⚛️',
      py: '🐍',
      html: '🌐',
      css: '🎨',
      json: '📋',
      md: '📝',
      png: '🖼️',
      jpg: '🖼️',
      svg: '🎨',
      git: '🔧'
    };
    return iconMap[ext] || '📄';
  };

  const filteredFiles = filterFiles(files, searchQuery);
  const fileTree = buildFileTree(filteredFiles);

  return (
    <div className="h-full flex flex-col bg-gray-900 border-r border-gray-700">
      {/* Tabs */}
      <div className="flex border-b border-gray-700 bg-gray-950">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-blue-400 border-b-2 border-blue-500 bg-gray-900'
                : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
            </svg>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'files' && (
          <div className="flex flex-col h-full">
            {/* Project Selector */}
            <div className="p-3 border-b border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-400 uppercase">Project</span>
                <button
                  onClick={onCreateProject}
                  className="text-blue-400 hover:text-blue-300 text-xs"
                >
                  + New
                </button>
              </div>
              <select
                value={currentProject?.id || ''}
                onChange={(e) => {
                  const project = projects.find(p => p.id === e.target.value);
                  if (project) onSelectProject(project);
                }}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-300"
              >
                <option value="">Select a project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>

              {/* Quick Actions */}
              <div className="flex gap-2 mt-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      onUploadFile(e.target.files[0]);
                      e.target.value = ''; // Reset
                    }
                  }}
                />
                <button
                  onClick={onImportGitHub}
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-800 hover:bg-gray-700 rounded text-xs text-gray-300"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  Import
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-800 hover:bg-gray-700 rounded text-xs text-gray-300"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload
                </button>
                <button
                  onClick={onOpenTemplates}
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded text-xs text-white font-medium"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                  Templates
                </button>
              </div>
            </div>

            {/* File Search */}
            <div className="p-3 border-b border-gray-800">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search files..."
                  className="w-full bg-gray-800 border border-gray-700 rounded pl-8 pr-3 py-2 text-sm text-gray-300 placeholder-gray-500"
                />
                <svg className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* File Tree */}
            <div className="flex-1 overflow-auto">
              {currentProject ? (
                <div className="py-2">
                  <div className="flex items-center justify-between px-3 py-1 mb-1">
                    <span className="text-xs font-semibold text-gray-400 uppercase">Files</span>
                    <button
                      onClick={onCreateFile}
                      className="text-blue-400 hover:text-blue-300 text-xs"
                    >
                      + New File
                    </button>
                  </div>
                  {filteredFiles.length > 0 ? (
                    renderFileTree(fileTree)
                  ) : (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      {searchQuery ? 'No files found' : 'No files in project'}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                  <svg className="w-16 h-16 text-gray-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <p className="text-gray-500 text-sm mb-4">No project selected</p>
                  <button
                    onClick={onCreateProject}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
                  >
                    Create New Project
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'ai' && (
          <div className="p-3">
            <div className="text-sm text-gray-400 mb-4">AI Assistant Panel</div>
            {/* AI content will be rendered here */}
          </div>
        )}

        {activeTab === 'build' && (
          <div className="p-3">
            <div className="text-sm text-gray-400 mb-4">Build & Debug Panel</div>
            {/* Build content will be rendered here */}
          </div>
        )}

        {activeTab === 'git' && (
          <div className="p-4 flex flex-col h-full items-center justify-center text-center">
            {currentProject?.github_url ? (
              <div className="space-y-4">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </div>
                <h3 className="text-gray-200 font-medium text-sm">GitHub Integration</h3>
                <p className="text-gray-500 text-xs px-4">
                  Sync local changes with remote branch <strong>{currentProject.github_branch || 'main'}</strong>.
                </p>
                
                <div className="pt-4 w-full">
                  <button
                    onClick={onOpenSync}
                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Sync to GitHub
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <svg className="w-12 h-12 text-gray-700 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-500 text-xs px-4">This project is not linked to GitHub yet.</p>
                <button
                  onClick={onImportGitHub}
                  className="text-blue-400 hover:text-blue-300 text-xs font-medium"
                >
                  Import from GitHub
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModernSidebar;
